import { Tooltip } from 'antd';
import {
  AssignmentCategory,
  StudentProgressBookSubject,
  StudentProgressBookSubjectLesson,
} from '../../../constants/types/student/homework/type';
import TextWinds from '../../common/TextWinds';
import { useAppDispatch } from '../../../store/hook';
import { openAlert } from '../../../store/features/modalSlice';
import {
  openTodoView,
  resolveJebsplayerUrl,
} from '../../../util/openContentsWindow';
import { useState } from 'react';

interface BookSubjectProgressRowProps {
  selectedCategory: AssignmentCategory;
  subject: StudentProgressBookSubject;
}

type LessonProgressStatus = 'COMPLETED' | 'IN_PROGRESS' | 'NOT_ASSIGNED';

const CELL_CLASS_MAP: Record<LessonProgressStatus, string> = {
  COMPLETED: 'border-green-200 bg-green-100 text-green-700',
  IN_PROGRESS: 'border-blue-200 bg-blue-100 text-[#0B8ED0]',
  NOT_ASSIGNED: 'border-gray3 bg-gray0 text-gray7',
};

const getLessonStatus = ({
  lesson,
}: {
  lesson: StudentProgressBookSubjectLesson;
}): LessonProgressStatus => {
  if (lesson.status === 'COMPLETED') {
    return 'COMPLETED';
  }

  /** [시작 전]도 화면에서는 [진행 중]으로 표시 */
  if (lesson.status === 'NOT_STARTED') {
    return 'IN_PROGRESS';
  }

  if (lesson.status === 'IN_PROGRESS') {
    return 'IN_PROGRESS';
  }

  return 'NOT_ASSIGNED';
};

const getFirstNumberText = (value: string) => {
  const matchedNumber = value.match(/\d+/)?.[0];

  return matchedNumber ? String(Number(matchedNumber)) : value;
};

const getLessonNumberText = (
  lessonTitle: string,
  selectedCategory: AssignmentCategory,
) => {
  if (selectedCategory === 'JEBSON') {
    if (/test/i.test(lessonTitle)) {
      return 'T';
    }

    const rangeMatchedNumber = lessonTitle.match(
      /(?:잉포플|잉스바|잉크|잉포|잉코)\s*\d+\s*-\s*0*(\d+)/i,
    )?.[1];

    if (rangeMatchedNumber) {
      return String(Number(rangeMatchedNumber));
    }

    return getFirstNumberText(lessonTitle);
  }

  if (selectedCategory === 'WMW') {
    if (/speech/i.test(lessonTitle)) {
      return 'S';
    }

    const lessonMatchedNumber = lessonTitle.match(/lesson\s*0*(\d+)/i)?.[1];

    if (lessonMatchedNumber) {
      return String(Number(lessonMatchedNumber));
    }

    return getFirstNumberText(lessonTitle);
  }

  if (selectedCategory === 'HFHJ') {
    if (/review/i.test(lessonTitle)) {
      return 'R';
    }

    return getFirstNumberText(lessonTitle);
  }

  return '-';
};

/**
 * 개편 LMS (학생) - 클래스 상세 페이지 > 콘텐츠 판넬 > 우측 젭스온(AI인증제 제외), WMW, HFHJ [진도 지도] > Book 카드 > Subject 행
 */
const BookSubjectProgressRow = ({
  subject,
  selectedCategory,
}: BookSubjectProgressRowProps) => {
  const dispatch = useAppDispatch();

  const setTodoView = useState<Window>(undefined)[1];

  const handleShowTestResult = (lesson: StudentProgressBookSubjectLesson) => {
    if (!lesson.link) {
      dispatch(
        openAlert({
          message: '과제 링크가 없습니다. 관리자에게 문의해주세요.',
        }),
      );
      return;
    }

    openTodoView({ url: resolveJebsplayerUrl(lesson.link) }, setTodoView);
  };

  return (
    <div className="grid grid-cols-[58px_1fr] items-center gap-3 xl:grid-cols-[90px_1fr]">
      <TextWinds type="content_body3" className={'font-bold text-gray8'}>
        {subject.subjectName}
      </TextWinds>

      <div className="flex flex-wrap gap-2">
        {subject.lessons.map((lesson, index) => {
          const status = getLessonStatus({
            lesson,
          });

          const lessonTitle = lesson.lesson
            ? `${lesson.lesson} · ${lesson.title}`
            : lesson.title;

          const canOpenLesson =
            lesson.status === 'COMPLETED' &&
            Boolean(lesson.link) &&
            selectedCategory === 'JEBSON' &&
            /test/i.test(lesson.title ?? '');

          const tooltipTitle = canOpenLesson ? (
            <div className="flex flex-col gap-1">
              <span>{lessonTitle}</span>
              <span className="text-center text-xs opacity-80">
                클릭하여 결과 확인
              </span>
            </div>
          ) : (
            lessonTitle
          );

          return (
            <Tooltip
              key={index}
              title={tooltipTitle}
              placement="top"
              zIndex={10002}
            >
              {canOpenLesson ? (
                <button
                  type="button"
                  className={[
                    'flex h-9 w-9 items-center justify-center rounded-lg border text-sm font-bold',
                    'transition-transform hover:scale-105 hover:shadow-sm active:scale-95',
                    'cursor-pointer',
                    CELL_CLASS_MAP[status],
                  ].join(' ')}
                  onClick={() => handleShowTestResult(lesson)}
                >
                  {getLessonNumberText(lesson.title, selectedCategory)}
                </button>
              ) : (
                <span
                  className={[
                    'flex h-9 w-9 cursor-default items-center justify-center rounded-lg border text-sm font-bold',
                    CELL_CLASS_MAP[status],
                  ].join(' ')}
                >
                  {getLessonNumberText(lesson.title, selectedCategory)}
                </span>
              )}
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

export default BookSubjectProgressRow;
